import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper';

export interface ContributionDrive {
  id: number;
  title: string;
  type?: string;
  amount_per_member?: number;
  target_amount?: number;
  status: 'active' | 'closed' | 'cancelled';
  start_date?: string;
  due_date?: string;
  created_at: string;
  updated_at: string;
  group_id: number;
  template_id?: number;
  recipient_id?: number;
  closed_at?: string;
  mpesa_credential_id?: number | null;
  mpesa_credential?: any;
}

export interface CreateContributionDriveData {
  title: string;
  type?: string;
  amount_per_member?: number;
  target_amount?: number;
  status?: string;
  subgroup_targets?: any;
  mpesa_credential_id?: number | null;
}

export interface UpdateContributionDriveData extends CreateContributionDriveData {
  id: number;
}

// Get all contribution drives
export const useContributionDrives = () => {
  return useQuery({
    queryKey: ['contribution-drives'],
    queryFn: async () => {
      const { data } = await api.get('/contribution-drives');
      return data;
    },
  });
};

// Get single contribution drive
export const useContributionDrive = (id: number) => {
  return useQuery({
    queryKey: ['contribution-drive', id],
    queryFn: async () => {
      const { data } = await api.get(`/contribution-drives/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

// Create contribution drive
export const useCreateContributionDrive = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (driveData: CreateContributionDriveData) => {
      const { data } = await api.post('/contribution-drives', driveData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contribution-drives'] });
      toast.success('Contribution category created successfully');
    },
    onError: (error) => {
      const message = getApiErrorMessage(error);
      toast.error(message || 'Failed to create contribution category');
    },
  });
};

// Update contribution drive
export const useUpdateContributionDrive = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...driveData }: UpdateContributionDriveData) => {
      const { data } = await api.put(`/contribution-drives/${id}`, driveData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contribution-drives'] });
      toast.success('Contribution category updated successfully');
    },
    onError: (error) => {
      const message = getApiErrorMessage(error);
      toast.error(message || 'Failed to update contribution category');
    },
  });
};

// Delete contribution drive
export const useDeleteContributionDrive = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/contribution-drives/${id}`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contribution-drives'] });
      toast.success(data?.message || 'Contribution category deleted successfully');
    },
    onError: (error) => {
      const message = getApiErrorMessage(error);
      toast.error(message || 'Failed to delete contribution category');
    },
  });
};